import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Menu, 
  MenuItem, 
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useAppData } from '../../contexts/AppDataContext';
import OrganizationSwitcher from '../organizations/OrganizationSwitcher';
import ProjectSwitcher from '../projects/ProjectSwitcher';

const NavBar = ({ handleDrawerToggle }) => {
  const { user, signOut } = useAuth();
  const { startupData } = useAppData();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await signOut();
    navigate('/login');
  };

  const handleProfile = () => {
    setAnchorEl(null);
    // Defer navigation until after the menu portal has fully unmounted.
    // Without this delay, React batches the state update with the route change
    // and the MUI backdrop can be left stuck on screen blocking all clicks.
    setTimeout(() => navigate('/account'), 0);
  };

  const handleOrganizationSettings = () => {
    navigate('/organization/settings');
  };


  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - 240px)` },
        ml: { sm: '240px' },
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <svg width="36" height="36" viewBox="8 14 138 138" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0, marginRight: 10 }}>
            <defs>
              <clipPath id="nav-body-clip">
                <path d="M48 18 L68 18 L88 100 L28 100 Z"/>
              </clipPath>
            </defs>
            <path d="M28 100 L88 100 L98 140 L18 140 Z" fill="#A87048"/>
            <path d="M48 18 L68 18 L88 100 L28 100 Z" fill="#241508"/>
            <rect x="54.5" y="22" width="7" height="78" fill="#EDE0C4" clipPath="url(#nav-body-clip)"/>
            <path d="M48 18 L68 18 L98 140 L18 140 Z" fill="none" stroke="#7B5030" strokeWidth="1.7" strokeLinejoin="round"/>
            <rect x="21" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
            <rect x="50.5" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
            <rect x="80" y="140" width="15" height="8" rx="4" fill="#7B5030"/>
            <line x1="58" y1="100" x2="135.8" y2="45.5" stroke="#9B7050" strokeWidth="2.6" strokeLinecap="round"/>
            <circle cx="58" cy="100" r="3.5" fill="#9B7050"/>
            <path d="M100.2 60.1 L110.0 74.1 L95.1 80.8 L88.8 71.8 Z" fill="#C07838" stroke="#7B5030" strokeWidth="1.4" strokeLinejoin="round"/>
          </svg>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
            <span style={{ color: '#241508' }}>Release</span>
            <span style={{ color: '#7B5030' }}> Cadence</span>
          </Typography>
        </Box>

        {/* Organization Switcher — no tooltip wrapper; the dropdown itself is the affordance */}
        <Box sx={{ mr: 2 }}>
          <OrganizationSwitcher onSettingsClick={handleOrganizationSettings} />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Project Switcher */}
        <Tooltip
          title="Switch active project"
          arrow
          placement="bottom"
          enterDelay={800}
          leaveDelay={0}
        >
          <Box sx={{ mr: 2, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
            <ProjectSwitcher />
          </Box>
        </Tooltip>

        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              {(startupData?.avatar_url || user.user_metadata?.avatar_url) ? (
                <Avatar
                  src={startupData?.avatar_url || user.user_metadata?.avatar_url}
                  alt={user.email}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleProfile}>Account Settings</MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box>
            <IconButton 
              color="inherit" 
              onClick={() => navigate('/login')}
              aria-label="login"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
